/**
 * ClientOnlyAuthSideEffects
 * 
 * Componente che monta gli hooks di side-effect dell'autenticazione
 * SOLO dopo che l'hydration è completa (isSafeToMountSideEffects === true).
 * 
 * 🛡️ Questo previene:
 * - React error #421 ("Suspense boundary received an update before it finished hydrating")
 * - OneSignal error "Cannot read properties of undefined (reading 'tt')"
 * 
 * Il componente viene renderizzato condizionalmente in AppInner
 * solo quando isSafeToMountSideEffects === true (double-defer pattern).
 * 
 * Include:
 * - AuthRedirects (navigazione auth-based)
 * - AuthSync (sincronizza auth state -> Zustand store)
 * - useAuthSync (OneSignal external_user_id sync)
 * - useSimplifiedPricePolling (polling prezzi di mercato)
 * - useAutoPreloader (precaricamento dati metalli) - 🛡️ MOVED HERE from App.tsx
 */

import { useAuthSync } from '@/hooks/useAuthSync';
import AuthSync from '@/components/AuthSync';
import { AuthRedirects } from '@/contexts/auth/AuthRedirects';
import { useSimplifiedPricePolling } from '@/hooks/useSimplifiedPricePolling';
import { useSessionExpiryWarning } from '@/hooks/useSessionExpiryWarning';

import { useStore } from '@/store/useStore';
import { useEffect } from 'react';
import { debugLog } from '@/utils/debugLogger';

export function ClientOnlyAuthSideEffects() {
  // Monta useAuthSync (OneSignal external_user_id sync) solo post-hydration
  useAuthSync();
  
  // 🎯 Price polling ora montato SOLO post-hydration per evitare #421
  useSimplifiedPricePolling();

  // ⏰ Avviso scadenza sessione (~5 min prima) con azione "Estendi"
  useSessionExpiryWarning();

  
  
  // 🛡️ FIX: Zustand rehydrate SOLO dopo che hydration è completa
  // Merge-safe nello store: i prezzi SSR non vengono sovrascritti da storage
  useEffect(() => {
    debugLog('general', 'Zustand rehydrate triggered (post-hydration safe)', undefined, 'ClientOnlyAuthSideEffects');
    Promise.resolve(useStore.persist.rehydrate() as any).catch(() => {
      // ignore
    });
  }, []);
  
  // Renderizza AuthRedirects e AuthSync per side effects
  return (
    <>
      {/* AuthRedirects gestisce navigazione basata su auth state */}
      <AuthRedirects />
      {/* AuthSync sincronizza auth state -> Zustand store */}
      <AuthSync />
    </>
  );
}

export default ClientOnlyAuthSideEffects;
